/**
 * useSessionTimeout Hook
 *
 * Tracks user activity and warns before session timeout.
 * Shows a warning dialog when the user has been inactive for too long.
 *
 * Note: Firebase Auth tokens are automatically refreshed, but this hook
 * provides UX for users who leave the app open without interaction.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';

// Session timeout settings (in milliseconds)
const INACTIVITY_WARNING_TIME = 55 * 60 * 1000; // 55 minutes - show warning
const INACTIVITY_LOGOUT_TIME = 60 * 60 * 1000; // 60 minutes - auto logout
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;

interface UseSessionTimeoutReturn {
  /** Whether the warning dialog should be shown */
  showWarning: boolean;
  /** Minutes remaining before auto-logout */
  minutesRemaining: number;
  /** Dismiss the warning and reset the timer */
  extendSession: () => void;
  /** Log out immediately */
  logoutNow: () => void;
}

export function useSessionTimeout(): UseSessionTimeoutReturn {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(5);

  const lastActivityRef = useRef<number>(Date.now());
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Reset activity timestamp
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);

    // Clear existing timeouts
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
  }, []);

  // Extend session (called when user clicks "Stay logged in")
  const extendSession = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  // Logout immediately
  const logoutNow = useCallback(() => {
    resetActivity();
    void logout();
  }, [logout, resetActivity]);

  // Set up activity listeners
  useEffect(() => {
    if (!user) return;

    const handleActivity = () => {
      // Only reset if warning is not showing (to prevent accidental dismissal)
      if (!showWarning) {
        resetActivity();
      }
    };

    // Add activity listeners
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [user, showWarning, resetActivity]);

  // Set up timeout checks
  useEffect(() => {
    if (!user) return;

    const checkInactivity = () => {
      const now = Date.now();
      const inactiveTime = now - lastActivityRef.current;

      if (inactiveTime >= INACTIVITY_LOGOUT_TIME) {
        // Auto logout
        void logout();
      } else if (inactiveTime >= INACTIVITY_WARNING_TIME && !showWarning) {
        // Show warning
        setShowWarning(true);

        // Start countdown
        const remaining = Math.ceil((INACTIVITY_LOGOUT_TIME - inactiveTime) / 60000);
        setMinutesRemaining(remaining);

        countdownIntervalRef.current = setInterval(() => {
          const newRemaining = Math.ceil(
            (INACTIVITY_LOGOUT_TIME - (Date.now() - lastActivityRef.current)) / 60000
          );
          setMinutesRemaining(Math.max(0, newRemaining));

          if (newRemaining <= 0) {
            void logout();
          }
        }, 30000); // Update every 30 seconds
      }
    };

    // Check every minute
    const intervalId = setInterval(checkInactivity, 60000);

    // Initial check
    checkInactivity();

    return () => {
      clearInterval(intervalId);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [user, showWarning, logout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []);

  return {
    showWarning,
    minutesRemaining,
    extendSession,
    logoutNow,
  };
}
